'use client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type AbstractIntlMessages, NextIntlClientProvider } from 'next-intl';
import React, { PropsWithChildren, useMemo } from 'react';

import { MovieLandingPageConfiguration } from '@cineamo/api/models/premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

import {
    CINEAMO_GTAG_ID,
    FB_PIXEL_EVENT_MAP,
    GA4_EVENT_MAP,
    REDDIT_EVENT_MAP,
    TIKTOK_EVENT_MAP,
    toSnakeCase
} from '@/constants/analytics-events';

import AnalyticsProvider from '@cineamo/composables/src/providers/analyticsProvider';

import { DomainConfigurationProvider } from './DomainConfigurationProvider';

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
        fbq?: (...args: unknown[]) => void;
        rdt?: (...args: unknown[]) => void;
        ttq?: {
            track: (event: string, data?: Record<string, unknown>) => void;
            grantConsent: () => void;
            revokeConsent: () => void;
        };
    }
}

const queryClient = new QueryClient();

const Providers: React.FC<
    PropsWithChildren<{
        locale: string;
        messages: AbstractIntlMessages;
        movieConfiguration: MovieLandingPageConfiguration;
    }>
> = ({ children, locale, messages, movieConfiguration }) => {
    const { gTagId, gtmContainerId, facebookPixelId, tiktokPixelId, redditPixelId } =
        movieConfiguration.domainConfiguration;

    const trackers = useMemo(() => {
        const list: Array<(event: string, data?: Record<string, unknown>) => void> = [];

        const gTagIds = [...new Set([CINEAMO_GTAG_ID, gTagId].filter((id): id is string => Boolean(id)))];
        if (gTagIds.length > 0) {
            list.push((event, data) => {
                const ga4Event = GA4_EVENT_MAP[event] ?? toSnakeCase(event);
                window.gtag?.('event', ga4Event, { ...data, send_to: gTagIds });
            });
        }

        if (facebookPixelId) {
            list.push((event, data) => {
                const config = FB_PIXEL_EVENT_MAP[event];
                if (config?.isStandard) {
                    window.fbq?.('track', config.eventName, config.mapParams?.(data ?? {}) ?? data);
                } else {
                    window.fbq?.('trackCustom', config?.eventName ?? event, data);
                }
            });
        }

        if (tiktokPixelId) {
            list.push((event, data) => {
                const config = TIKTOK_EVENT_MAP[event];
                window.ttq?.track(config?.eventName ?? event, config?.mapParams?.(data ?? {}) ?? data);
            });
        }

        if (redditPixelId) {
            list.push((event, data) => {
                const config = REDDIT_EVENT_MAP[event];
                if (config) {
                    window.rdt?.('track', config.eventName, config.mapParams?.(data ?? {}) ?? data);
                } else {
                    window.rdt?.('track', 'Custom', { ...data, customEventName: event });
                }
            });
        }

        if (gtmContainerId) {
            // The distributor manages their own tags inside the container, so we only make the
            // page's events available on the dataLayer under their GA4 names and let them
            // decide what to do with them.
            list.push((event, data) => {
                window.dataLayer?.push({ ...data, event: GA4_EVENT_MAP[event] ?? toSnakeCase(event) });
            });
        }

        return list;
    }, [gTagId, gtmContainerId, facebookPixelId, tiktokPixelId, redditPixelId]);

    return (
        <NextIntlClientProvider
            locale={locale}
            messages={messages}
            timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}>
            <DomainConfigurationProvider domainConfiguration={movieConfiguration}>
                <QueryClientProvider client={queryClient}>
                    <AnalyticsProvider
                        overallData={{
                            movieTmdbId: String(movieConfiguration.movie.tmdbId),
                            movieTitle: movieConfiguration.movie.title ?? ''
                        }}
                        eventPrefix=""
                        trackers={trackers}>
                        {children}
                    </AnalyticsProvider>
                </QueryClientProvider>
            </DomainConfigurationProvider>
        </NextIntlClientProvider>
    );
};

export default Providers;
